import type { Locale } from '@t4s/api-client/model'
import { type Ref, useId, useMemo, useRef } from 'react'
import { canonicalTimeZone, timeZoneNames } from './time-zone.ts'

/**
 * The three configuration fields the create form (§8.2) and the settings form (§12) share.
 *
 * They are shared rather than written twice because the rules are the same in both places
 * and one of them is easy to get subtly wrong: **the enabled set must contain the default,
 * and unchecking the default is refused in the form** with the reason on the checkbox,
 * rather than by the server (§12).
 *
 * **`chore-platform-field-error-mapping`** gave each field an optional `error` (the locale
 * pair gets one each, `defaultLocaleError`/`enabledLocalesError`, since they are two
 * separate request fields even though one component draws both) — the line a caller's own
 * `fieldErrorsOf` (`lib/api-error.ts`) resolved for it from a `validation_failed`'s
 * `details[]`. Rendered as `.field-error`, wired with `aria-describedby`, exactly as the
 * create form's own slug field already did before either of these had one. `undefined`
 * renders nothing, so every existing caller is unaffected until it starts passing one.
 */

/**
 * The timezone field — a searchable list, **not** free text.
 *
 * `docs/DECISIONS.md`, "Zones come from `Intl`, and a fixed-offset zone is not a zone": the
 * options come from the browser's own zone list, so the console cannot offer a value the
 * server will refuse. A `<datalist>` gives a real `<input>` a searchable set of suggestions
 * without a custom widget — which is what §21 asks for, since no custom widget here may
 * replace a native one.
 *
 * A runtime without `supportedValuesOf` (an older browser) falls back to a short list with
 * the default in it, so the field is never empty and never blocks the form.
 */
export function TimeZoneField({
  value,
  onChange,
  disabled = false,
  error,
  inputRef,
}: {
  readonly value: string
  readonly onChange: (value: string) => void
  readonly disabled?: boolean
  readonly error?: string | undefined
  /** For a screen that has to put focus here when the field is the remedy for an error. */
  readonly inputRef?: Ref<HTMLInputElement>
}) {
  const fieldId = useId()
  const listId = useId()
  const helpId = useId()
  const zones = useTimeZones()
  // True from the first keystroke until the field loses focus: only a value the owner typed is
  // corrected on blur, so tabbing past a stored value never edits it.
  const typing = useRef(false)

  return (
    <div className="field">
      <label htmlFor={fieldId}>Timezone</label>
      <input
        id={fieldId}
        ref={inputRef}
        list={listId}
        value={value}
        disabled={disabled}
        aria-invalid={error !== undefined}
        aria-describedby={helpId}
        onChange={(event) => {
          typing.current = true
          onChange(event.target.value)
        }}
        onBlur={() => {
          if (!typing.current) {
            return
          }
          typing.current = false
          // The zone in the browser's own casing, once the owner has finished typing it
          // (`europe/kyiv` → `Europe/Kyiv`): the API stores whatever it is sent. Not while typing,
          // which would move the owner's cursor.
          const canonical = canonicalTimeZone(value)
          if (canonical !== value) {
            onChange(canonical)
          }
        }}
      />
      <datalist id={listId}>
        {zones.map((zone) => (
          <option key={zone} value={zone} />
        ))}
      </datalist>
      <div id={helpId}>
        {error === undefined ? null : <p className="field-error">{error}</p>}
        <p className="muted">
          An IANA name such as Europe/Berlin. This decides what “today” means for this tenant: day
          boundaries, the assignment calendar, export ranges and the nightly expiry sweep are all
          computed in it.
        </p>
      </div>
    </div>
  )
}

function useTimeZones(): readonly string[] {
  return useMemo(() => timeZoneNames(), [])
}

/**
 * The default locale and the enabled set (§8.2, §12).
 *
 * Checkboxes rather than a multi-select: four options do not need a picker. Checking the
 * default is not optional, and the form says why on the checkbox rather than letting the
 * server refuse it.
 */
export function LocaleFields({
  locales,
  labels,
  defaultLocale,
  enabledLocales,
  onDefaultChange,
  onEnabledChange,
  disabled = false,
  defaultLocaleError,
  enabledLocalesError,
}: {
  readonly locales: readonly Locale[]
  readonly labels: Readonly<Record<Locale, string>>
  readonly defaultLocale: Locale
  readonly enabledLocales: readonly Locale[]
  readonly onDefaultChange: (locale: Locale) => void
  readonly onEnabledChange: (locales: Locale[]) => void
  readonly disabled?: boolean
  readonly defaultLocaleError?: string | undefined
  readonly enabledLocalesError?: string | undefined
}) {
  const defaultId = useId()
  const defaultHelpId = useId()
  const enabledHelpId = useId()

  const toggle = (locale: Locale, checked: boolean) => {
    if (!checked && locale === defaultLocale) {
      return
    }
    onEnabledChange(
      checked
        ? [...enabledLocales, locale]
        : enabledLocales.filter((enabled) => enabled !== locale),
    )
  }

  return (
    <>
      <div className="field field-narrow">
        <label htmlFor={defaultId}>Default language</label>
        <select
          id={defaultId}
          value={defaultLocale}
          disabled={disabled}
          aria-invalid={defaultLocaleError !== undefined}
          aria-describedby={defaultHelpId}
          onChange={(event) => {
            const next = event.target.value as Locale
            onDefaultChange(next)
            // Changing the default into a language that is not enabled would produce a
            // tenant whose default nobody can select, so enabling it is part of the same
            // action rather than a second error message.
            if (!enabledLocales.includes(next)) {
              onEnabledChange([...enabledLocales, next])
            }
          }}
        >
          {locales.map((locale) => (
            <option key={locale} value={locale}>
              {labels[locale]}
            </option>
          ))}
        </select>
        <div id={defaultHelpId}>
          {defaultLocaleError === undefined ? null : (
            <p className="field-error">{defaultLocaleError}</p>
          )}
          <p className="muted">What this tenant’s staff get before they choose one.</p>
        </div>
      </div>

      <fieldset className="field" aria-describedby={enabledHelpId}>
        <legend>Available languages</legend>
        <div className="check-grid">
          {locales.map((locale) => (
            <label key={locale} className="checkbox">
              <input
                type="checkbox"
                checked={enabledLocales.includes(locale)}
                disabled={disabled || locale === defaultLocale}
                onChange={(event) => {
                  toggle(locale, event.target.checked)
                }}
              />
              {labels[locale]}
              {locale === defaultLocale ? (
                <span className="muted"> — the default, so it is always available</span>
              ) : null}
            </label>
          ))}
        </div>
        <div id={enabledHelpId}>
          {enabledLocalesError === undefined ? null : (
            <p className="field-error">{enabledLocalesError}</p>
          )}
          <p className="muted">
            Removing a language is not destructive: somebody whose chosen language is no longer
            available falls back to this tenant’s default the next time they open the app.
          </p>
        </div>
      </fieldset>
    </>
  )
}
